<template>
  <el-dialog
    :title="displayText.title"
    :visible="true"
    width="700px"
    :close-on-click-modal="false"
    @close="$emit('close')"
  >
    <ElInputWrapper>
      <header
        class="grid"
        style="gap: 16px"
        :class="{
          'grid-cols-2': !isBenefitEnabled,
          'grid-cols-3': isBenefitEnabled,
        }"
      >
        <BaseElInput v-model="search.name" :placeholder="displayText.name.placeholder" clearable>
          <i
            slot="suffix"
            class="el-input__icon el-icon-search"
          />
        </BaseElInput>
        <BaseElSelect v-model="search.type" :placeholder="displayText.type.placeholder" clearable>
          <BaseElSelectOption v-for="option in typeOptions" :key="option.value" :label="option.label" :value="option.value" />
        </BaseElSelect>
        <BaseElSelect
          v-if="isBenefitEnabled"
          v-model="search.sharingType"
          placeholder="請選擇通用類型"
          clearable
          style="width: 180px"
        >
          <BaseElSelectOption label="分店專屬" value="shop" />
          <BaseElSelectOption label="組織通用" value="org" />
        </BaseElSelect>
      </header>
    </ElInputWrapper>

    <main>
      <div class="grid-container text-primary-100" :class="{ 'grid-org': isBenefitEnabled }" style="padding-top: 21px; padding-bottom: 16px">
        <BaseElCheckbox :label="displayText.table.label" :disabled="!multiple" @change="selectAll">
          <p class="text-primary-100">{{ displayText.table.name }}</p>
        </BaseElCheckbox>
        <p class="text-center">{{ displayText.table.type }}</p>
        <p class="text-center">{{ displayText.table.exp }}</p>
        <p v-if="isBenefitEnabled" class="text-center">發行店</p>
        <p v-if="isBenefitEnabled" class="text-center">到期提醒天數</p>
      </div>

      <BaseElCheckboxGroup v-model="selected">
        <div v-for="product in displayData[tableOptions.page - 1]" :key="product.id">
          <hr>
          <div class="grid-container" :class="{ 'grid-org': isBenefitEnabled }" style="padding: 16px 0;">
            <BaseElCheckbox :label="product.id" :disabled="disabledItem(product)">
              <div class="flex items-center relative min-w-0" style="gap: 4px">
                <el-tooltip effect="dark" :content="getSourceLabel(product)" placement="top">
                  <SVGIcon
                    :icon="getSourceIcon(product)"
                    stroke="var(--gray-80)"
                    fill="transparent"
                    width="20"
                    height="20"
                    class="flex-shrink-0"
                  />
                </el-tooltip>
                <el-tooltip effect="dark" :content="product.name" placement="top">
                  <span class="truncate">{{ product.name }}</span>
                </el-tooltip>
                <TipInfo v-if="disabledReason(product)" :size="20">
                  {{ disabledReason(product) }}
                </TipInfo>
              </div>
            </BaseElCheckbox>
            <el-tooltip effect="dark" :content="get(couponTypeConfig, `${product.type}.label`)" placement="top">
              <p class="text-center truncate">{{ get(couponTypeConfig, `${product.type}.label`) }}</p>
            </el-tooltip>
            <el-tooltip effect="dark" :content="formatExpPreviewText({ isExp: product.isExp, expType: product.expType, expDay: product.expDay, endOfPeriodUnit: product.endOfPeriodUnit, expAt: product.expAt, specifyExpiredDate: product.specifyExpiredDate })" placement="top">
              <p class="text-center truncate">
                {{ formatExpPreviewText({
                  isExp: product.isExp,
                  expType: product.expType,
                  expDay: product.expDay,
                  endOfPeriodUnit: product.endOfPeriodUnit,
                  expAt: product.expAt,
                  specifyExpiredDate: product.specifyExpiredDate,
                }) }}
              </p>
            </el-tooltip>
            <el-tooltip v-if="isBenefitEnabled" effect="dark" :content="product.Shop?.name || '-'" placement="top">
              <p class="text-center truncate">{{ product.Shop?.name || '-' }}</p>
            </el-tooltip>
            <el-tooltip v-if="isBenefitEnabled" effect="dark" :content="product.expNotifyDay != null ? `${product.expNotifyDay} 天` : '-'" placement="top">
              <p class="text-center">{{ product.expNotifyDay != null ? `${product.expNotifyDay} 天` : '-' }}</p>
            </el-tooltip>
          </div>
        </div>
      </BaseElCheckboxGroup>
    </main>

    <div>
      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="flattenDeep(displayData).length"
      />
    </div>

    <div class="flex justify-end items-center" style="padding-top: 26px">
      <BaseElButton plain @click="$emit('close')">{{ displayText.button.cancel }}</BaseElButton>
      <BaseElButton type="primary" :disabled="!selected.length" @click="onSubmit">{{ displayText.button.confirm }}</BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import { defineComponent, onMounted, reactive, ref, computed, watch } from 'vue'
import { map, chunk, get, flattenDeep } from 'lodash'
import { useTable } from '@/use/table'
import { useCoupon } from '@/use/useCoupon'
import { couponTypeConfig } from '@/config/couponExchange'
import dayjs from '@/lib/dayjs'
import TipInfo from '../TipInfo.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { i18n } from '@/plugins/i18n/i18n'
import { couponInvalidReasonConfig } from '@/config/coupon'
import { useShop } from '@/use/shop'
import { isBenefitSharingEnabled } from '@/use/useOrgBenefitControl'
import ElInputWrapper from '@/components/ElInputWrapper.vue'
import store from '@/store'

export default defineComponent({
  name: 'CouponAdvanceSelect',
  components: { TipInfo, SVGIcon, ElInputWrapper },
  props: {
    typeOptions: {
      type: Object,
      default: () => ({ none: { label: '暫無資料', value: 'none' } }),
    },
    data: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    max: { type: [Number, null], default: null },
    disabledExp: Boolean,
    selectedData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'confirm'],
  setup (props, { emit }) {
    const { formatExpPreviewText } = useCoupon()
    const { tableData, tableDataCount, tableOptions } = useTable()
    const { shopId } = useShop()
    const isBenefitEnabled = computed(() => isBenefitSharingEnabled('coupon', shopId.value))

    const couponReminderRules = computed(() => {
      return get(store.state, 'shop.shopSetting.benefitSharing.coupon.reminderRules', [])
    })

    const enrichedData = computed(() => {
      if (!isBenefitEnabled.value) return props.data
      return props.data.map((item) => {
        if (item.expNotifyDay != null) return item
        const shopKey = item.Shop?.id || item.sourceShopId
        if (!shopKey) return item
        const rule = couponReminderRules.value.find(r => r.shopId === shopKey)
        const expireNotificationBefore = rule?.expireNotificationBefore
        if (expireNotificationBefore == null) return item
        return { ...item, expNotifyDay: expireNotificationBefore }
      })
    })

    const isOrgCoupon = (item) => {
      if (!item) return false
      if (item.isOrgItem != null) return Boolean(item.isOrgItem)
      if (item.sourceShopIsHeadquarter != null) return Boolean(item.sourceShopIsHeadquarter)
      if (item.benefitShopScope) return ['all', 'shared'].includes(item.benefitShopScope)
      if (item.sourceShopId) return item.sourceShopId !== shopId.value
      if (item.Shop?.isHeadquarter != null) return Boolean(item.Shop.isHeadquarter)
      return Boolean(item.Shop?.id && item.Shop.id !== shopId.value)
    }

    const getSourceIcon = (item) => {
      if (isOrgCoupon(item)) return 'common-org_icon'
      return 'common-shop_icon'
    }
    const getSourceLabel = (item) => {
      if (isOrgCoupon(item)) return '組織通用'
      return '分店專屬'
    }
    tableOptions.pageLimit = 8
    const displayData = computed(() => {
      let data = [...enrichedData.value]
      if (search.name) data = data.filter(i => i.name.includes(search.name))
      if (search.type) data = data.filter(i => i.type === search.type)
      if (search.sharingType) {
        data = data.filter((i) => {
          if (search.sharingType === 'shop') return !isOrgCoupon(i)
          return isOrgCoupon(i)
        })
      }
      return chunk(data, tableOptions.pageLimit)
    })
    const displayText = computed(() => {
      const data = {
        title: '選擇票券',
        name: {
          placeholder: '請輸入名稱',
        },
        type: {
          placeholder: '請選擇類型',
        },
        table: {
          label: '產品名稱',
          name: '名稱',
          type: '類型',
          exp: '有效期限',
        },
        button: {
          cancel: i18n.t('common.button.cancel.text'),
          confirm: i18n.t('common.button.confirm.text'),
        },
      }
      return data
    })
    const isOpentixDisabled = (product) => {
      if (product.type !== 'opentix') return false
      return get(product.extraStatus, 'opentixIsDisabled')
    }

    const disabledItem = computed(() => {
      return (product) => {
        const productId = product.id

        // 1. 如果是已選中的項目，永遠允許取消選中
        if (selected.value.includes(productId)) {
          return false
        }

        // 2. coupon 特有的禁用條件
        if (product.isRemove || product.isInvalid || isOpentixDisabled(product)) {
          return true
        }

        // 3. 檢查過期狀態（只對未選中項目生效）
        if (props.disabledExp && checkExp(product)) {
          return true
        }

        // 4. 檢查數量限制
        if (props.max && selected.value.length >= props.max) {
          return true
        }

        // 5. 單選模式下，如果已有選中項目，禁用其他項目
        if (!props.multiple && selected.value.length > 0) {
          return true
        }

        return false
      }
    })
    const search = reactive({
      name: null,
      type: null,
      sharingType: null,
    })
    const selected = ref([])

    // 計算目標頁面的函式
    const findTargetPage = (couponId) => {
      if (!couponId) return 1

      // 在完整資料列表中找到目標項目的索引
      let filteredData = [...enrichedData.value]

      // 套用篩選條件
      if (search.name) filteredData = filteredData.filter(i => i.name.includes(search.name))
      if (search.type) filteredData = filteredData.filter(i => i.type === search.type)
      if (search.sharingType) {
        filteredData = filteredData.filter((i) => {
          if (search.sharingType === 'shop') return !isOrgCoupon(i)
          return isOrgCoupon(i)
        })
      }

      const targetIndex = filteredData.findIndex(item => item.id === couponId)
      if (targetIndex === -1) return 1

      // 計算目標頁面（每頁 8 筆）
      return Math.ceil((targetIndex + 1) / tableOptions.pageLimit)
    }

    // 檢查選中的項目是否在當前篩選結果中
    const isSelectedItemVisible = (couponId) => {
      if (!couponId) return true

      let filteredData = [...enrichedData.value]
      if (search.name) filteredData = filteredData.filter(i => i.name.includes(search.name))
      if (search.type) filteredData = filteredData.filter(i => i.type === search.type)
      if (search.sharingType) {
        filteredData = filteredData.filter((i) => {
          if (search.sharingType === 'shop') return !isOrgCoupon(i)
          return isOrgCoupon(i)
        })
      }

      return filteredData.some(item => item.id === couponId)
    }

    // 初始化選中狀態的函式
    const initializeSelection = () => {
      if (props.selectedData && props.selectedData.length > 0) {
        // 檢查選中項目是否存在於資料中
        const firstSelectedId = props.selectedData[0]
        const itemExists = enrichedData.value.some(item => item.id === firstSelectedId)
        if (!itemExists) {
          return
        }

        // 設定選中狀態
        selected.value = props.selectedData

        // 切換到目標頁面（使用第一個選中項目）
        const targetPage = findTargetPage(firstSelectedId)
        tableOptions.page = targetPage
      }
    }

    const selectAll = (all) => {
      if (all) {
        selected.value = map(displayData.value[tableOptions.page - 1], 'id')
        if (props.max) selected.value = selected.value.slice(0, props.max)
      } else selected.value = []
    }

    const checkExp = (product) => {
      if (!props.disabledExp) return false

      // 「領取後 X 天」在發放前沒有確定的過期日，不應被視為已過期而禁用
      if (product.expType === 'expDay') {
        return false
      }

      const expDate = get(product, 'expAt') || get(product, 'specifyExpiredDate')
      if (!expDate) return false
      const isExp = dayjs(expDate).isSameOrBefore(dayjs())
      return isExp
    }

    const onSubmit = () => {
      emit('confirm', selected.value)
      emit('close')
    }

    const disabledReason = (item) => {
      if (checkExp(item)) return '已過期'
      else if (item.isRemove) return '該品項已被刪除'
      else if (isOpentixDisabled(item)) return 'opentix 該品項已被停用'
      else if (item.isInvalid) {
        return get(couponInvalidReasonConfig, `${item.invalidReasonCode}.label`)
      }
    }

    onMounted(() => {
      tableData.value = enrichedData.value
      tableDataCount.value = enrichedData.value.length

      // 初始化選中狀態和頁面
      initializeSelection()
    })

    // 監聽篩選條件變化，重新定位頁面
    watch([() => search.name, () => search.type, () => search.sharingType], () => {
      if (selected.value.length > 0) {
        const firstSelectedId = selected.value[0]

        // 檢查選中項目是否在篩選結果中
        if (isSelectedItemVisible(firstSelectedId)) {
          const targetPage = findTargetPage(firstSelectedId)
          tableOptions.page = targetPage
        } else {
          // 如果選中項目被篩選掉了，回到第一頁
          tableOptions.page = 1
        }
      }
    })

    return {
      get,
      flattenDeep,
      onSubmit,
      search,
      selected,
      selectAll,
      displayData,
      tableDataCount,
      tableOptions,

      dayjs,
      checkExp,
      disabledItem,
      formatExpPreviewText,
      couponTypeConfig,
      displayText,
      disabledReason,
      findTargetPage,
      initializeSelection,
      isSelectedItemVisible,
      getSourceIcon,
      getSourceLabel,
      isBenefitEnabled,
    }
  },
})
</script>

<style scoped lang="postcss">
.grid-container {
  @apply grid gap-[12px];
  @apply text-normal;
  grid-template-columns: 1fr 100px 200px 100px;
}
.grid-org {
  grid-template-columns: 1fr 80px 150px 110px 80px;
}
::v-deep .el-input {
  @apply w-full;
}
::v-deep .el-select {
  @apply w-full;
}
::v-deep .el-checkbox {
  @apply flex items-center w-[180px];
}
::v-deep .el-checkbox .el-checkbox__label {
  @apply truncate w-full flex items-center;
}
</style>
